import { useCurrentPageAccess } from "@/app/router";
import { ConfirmDialog } from "@/shared/components/feedback/ConfirmDialog";
import { Breadcrumbs } from "@/shared/components/layout/Breadcrumbs";
import { PageHeader } from "@/shared/components/layout/PageHeader";
import { DataTable } from "@/shared/components/table";
import { DataTableToolbar } from "@/shared/components/table/DataTableToolbar";
import { Button } from "@/shared/components/ui/button";
import { useDialog } from "@/shared/hooks/useDialog";
import { RotateCw } from "lucide-react";
import { useEffect, useState } from "react";
import { TmsTokenCreateDialog } from "../components/TmsTokenCreateDialog";
import { TmsTokenDetailsDialog } from "../components/TmsTokenDetailsDialog";
import { useTmsTokenColumns } from "../hooks/useTmsTokenColumns";
import { useTmsTokenManagement } from "../hooks/useTmsTokenManagement";
import type { TmsAccountRow } from "../types/tokens-types";
import type { SortingState } from "@tanstack/react-table";

export function TokensTmsTokenManagementPage() {
  const vm = useTmsTokenManagement();
  const columns = useTmsTokenColumns();
  const { canWriteCurrentPage } = useCurrentPageAccess();
  const [selectedRows, setSelectedRows] = useState<TmsAccountRow[]>([]);
  const detailsDialog = useDialog<TmsAccountRow>();
  const createDialog = useDialog<void>();
  const deleteDialog = useDialog<void>();

  // 페이지네이션 상태
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sorting, setSorting] = useState<SortingState>([]);

  // 페이지네이션/정렬/검색 변경 시 서버에서 데이터 재조회
  useEffect(() => {
    vm.fetchAccountsWithParams({
      page,
      page_size: pageSize,
      sort_by: sorting[0]?.id,
      sort_desc: sorting[0]?.desc,
      keyword: vm.searchValue || undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, sorting, vm.searchValue]);

  return (
    <div className="space-y-6">
      <Breadcrumbs />
      <PageHeader title="TMS 토큰 관리" description="TMS에 등록된 토큰을 조회하고 추가할 수 있습니다." />

      <DataTable
        columns={columns}
        data={vm.accounts}
        totalCount={vm.totalCount}
        page={page}
        pageSize={pageSize}
        sorting={sorting}
        onSortingChange={setSorting}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        enableRowSelection
        rowSelectionTrigger="checkbox"
        getRowId={(row) => String(row.accountId)}
        onRowSelectionChange={setSelectedRows}
        onRowClick={(row) => detailsDialog.open(row)}
        toolbar={
          <DataTableToolbar
            searchValue={vm.searchValue}
            onSearchChange={(value) => {
              vm.setSearchValue(value);
              setPage(1);
            }}
            showReset={!!vm.searchValue}
            onReset={() => {
              vm.setSearchValue("");
              setPage(1);
            }}
            searchPlaceholder="Search here..."
            actions={
              <>
                <Button variant="outline" onClick={() => vm.load()}>
                  <RotateCw className="mr-2 h-4 w-4" />
                  새로고침
                </Button>
                <Button variant="outline" onClick={() => createDialog.open()} disabled={!canWriteCurrentPage}>
                  추가
                </Button>
                <Button
                  variant="destructive"
                  disabled={selectedRows.length === 0 || !canWriteCurrentPage}
                  onClick={() => deleteDialog.open()}
                >
                  삭제
                </Button>
              </>
            }
          />
        }
      />

      <TmsTokenDetailsDialog
        open={detailsDialog.isOpen}
        onOpenChange={(open) => !open && detailsDialog.close()}
        tokens={detailsDialog.data?.tokens ?? []}
      />

      <TmsTokenCreateDialog
        open={createDialog.isOpen}
        onOpenChange={(open) => !open && createDialog.close()}
        channels={vm.channels}
        currentChannel={vm.currentChannel}
        schemaFields={vm.schema.fields}
        onChangeChannel={vm.fetchSchema}
        onSubmit={({ channel, fields }) => vm.createToken({ channel, fields })}
      />

      <ConfirmDialog
        open={deleteDialog.isOpen}
        onOpenChange={(open) => !open && deleteDialog.close()}
        title="삭제"
        description={`선택한 ${selectedRows.length}개의 토큰 계정을 삭제하시겠습니까?`}
        confirmText="삭제"
        variant="destructive"
        onConfirm={async () => {
          await vm.deleteAccounts(selectedRows);
          setSelectedRows([]);
          deleteDialog.close();
        }}
      />
    </div>
  );
}
